import React, { useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Importação de cadastro por planilha, em três passos: escolher o arquivo,
 * conferir a prévia (o que entra, o que já existe, o que está errado) e
 * confirmar. Quem decide duplicidade é o backend.
 *
 * Usado por fornecedores, clientes e usuários; cada tela passa o que é dela:
 * textos, como ler a planilha, as chamadas ao backend e as colunas da prévia.
 */

const SITUACAO = {
	novo: { rotulo: "Novo", cor: "#1e8e4e", fundo: "#e6f6ec", resumo: "novos" },
	reativar: { rotulo: "Reativar", cor: "#0f4557", fundo: "#e3f2f6", resumo: "reativar" },
	ja_cadastrado: { rotulo: "Já cadastrado", cor: "#5a7a88", fundo: "#eef2f4", resumo: "jaCadastrados" },
	repetido: { rotulo: "Repetido na planilha", cor: "#b26a00", fundo: "#fff4e5", resumo: "repetidos" },
	invalido: { rotulo: "Com erro", cor: "#c62828", fundo: "#fdecec", resumo: "invalidos" },
};

export const formatCpfCnpj = (value) => {
	if (!value) return "-";
	const v = value.replace(/\D/g, "");
	if (v.length === 11) return v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
	if (v.length === 14) return v.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
	return value;
};

export const formatTelefone = (value) => {
	if (!value) return "-";
	const d = value.replace(/\D/g, "");
	if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, "($1) $2-$3");
	if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, "($1) $2-$3");
	return value;
};

const cabecalhoSx = { fontWeight: 600, color: "#0f4557", fontFamily: "'Plus Jakarta Sans', sans-serif", whiteSpace: "nowrap" };

/**
 * @param {object} props
 * @param {string} props.titulo
 * @param {{ singular: string, plural: string, contagem: string }} props.entidade ex.: { singular: "fornecedor", plural: "fornecedores", contagem: "fornecedor(es)" }
 * @param {React.ReactNode} props.instrucoes texto do primeiro passo
 * @param {(file: File) => Promise<{ linhas: object[], colunasIgnoradas: string[] }>} props.ler
 * @param {() => void} props.baixarModelo
 * @param {(linhas: object[]) => Promise<{ linhas: object[], resumo: object }>} props.previa
 * @param {(linhas: object[]) => Promise<{ criados: number, reativados?: number, resumo: object }>} props.importar
 * @param {{ rotulo: string, render: (linha: object) => React.ReactNode, sx?: object }[]} props.colunas colunas da prévia entre "Linha" e "Situação"
 * @param {string} props.textoAvisos explica o que acontece com linha que tem aviso
 * @param {(previa: object) => React.ReactNode} [props.alertasExtras] alertas próprios da tela na prévia
 */
function ImportarPlanilhaDialog({
	open,
	onClose,
	onImportado,
	titulo,
	entidade,
	instrucoes,
	ler,
	baixarModelo,
	previa: analisar,
	importar,
	colunas,
	textoAvisos,
	alertasExtras,
}) {
	const inputRef = useRef(null);
	const [etapa, setEtapa] = useState("arquivo"); // arquivo | previa | resultado
	const [carregando, setCarregando] = useState(false);
	const [erro, setErro] = useState("");
	const [nomeArquivo, setNomeArquivo] = useState("");
	const [linhas, setLinhas] = useState([]);
	const [colunasIgnoradas, setColunasIgnoradas] = useState([]);
	const [previa, setPrevia] = useState(null);
	const [resultado, setResultado] = useState(null);

	const reiniciar = () => {
		setEtapa("arquivo");
		setCarregando(false);
		setErro("");
		setNomeArquivo("");
		setLinhas([]);
		setColunasIgnoradas([]);
		setPrevia(null);
		setResultado(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const fechar = () => {
		if (carregando) return;
		const importou = etapa === "resultado";
		reiniciar();
		onClose();
		if (importou) onImportado?.();
	};

	const handleArquivo = async (event) => {
		const file = event.target.files?.[0];
		if (!file) return;
		setErro("");
		setCarregando(true);
		setNomeArquivo(file.name);
		try {
			const lido = await ler(file);
			const analise = await analisar(lido.linhas);
			setLinhas(lido.linhas);
			setColunasIgnoradas(lido.colunasIgnoradas);
			setPrevia(analise);
			setEtapa("previa");
		} catch (e) {
			console.error(`Erro ao ler planilha de ${entidade.plural}:`, e);
			setErro(e.message || "Não foi possível ler a planilha.");
			if (inputRef.current) inputRef.current.value = "";
		} finally {
			setCarregando(false);
		}
	};

	const handleImportar = async () => {
		setErro("");
		setCarregando(true);
		try {
			const res = await importar(linhas);
			setResultado(res);
			setEtapa("resultado");
		} catch (e) {
			console.error(`Erro ao importar ${entidade.plural}:`, e);
			setErro(e.message || `Erro ao importar ${entidade.plural}.`);
		} finally {
			setCarregando(false);
		}
	};

	const resumo = previa?.resumo;
	const totalAImportar = (resumo?.novos || 0) + (resumo?.reativar || 0);

	return (
		<Dialog open={open} onClose={fechar} maxWidth={etapa === "previa" ? "lg" : "sm"} fullWidth>
			<DialogTitle sx={{ color: "#0f4557", fontWeight: 700, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
				{titulo}
			</DialogTitle>

			<DialogContent dividers>
				{erro && (
					<Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro("")}>
						{erro}
					</Alert>
				)}

				{etapa === "arquivo" && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Typography variant="body2" sx={{ color: "#0f4557" }}>
							Envie um arquivo <strong>.csv</strong>, <strong>.xlsx</strong> ou <strong>.xls</strong> com um {entidade.singular} por
							linha. A primeira linha da tabela deve ter o nome das colunas.
						</Typography>
						{instrucoes}

						<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1 }}>
							<Button
								variant="outlined"
								startIcon={<FileDownloadIcon />}
								onClick={baixarModelo}
								sx={{ borderColor: "#dfe3e8", color: "#0f4557", textTransform: "none", borderRadius: "10px" }}
							>
								Baixar modelo (.xlsx)
							</Button>
							<Button
								variant="contained"
								component="label"
								startIcon={carregando ? <CircularProgress size={18} sx={{ color: "white" }} /> : <UploadFileIcon />}
								disabled={carregando}
								sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#138a7d" }, textTransform: "none", borderRadius: "10px", fontWeight: 600 }}
							>
								{carregando ? "Lendo planilha..." : "Escolher arquivo"}
								<input
									ref={inputRef}
									type="file"
									hidden
									accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
									onChange={handleArquivo}
								/>
							</Button>
						</Box>
					</Box>
				)}

				{etapa === "previa" && previa && (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						<Typography variant="body2" sx={{ color: "#5a7a88" }}>
							Arquivo: <strong>{nomeArquivo}</strong> — {resumo.total} linha(s) lida(s)
						</Typography>

						<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
							{Object.entries(SITUACAO).map(([status, cfg]) => {
								const n = resumo[cfg.resumo];
								if (!n) return null;
								return (
									<Chip
										key={status}
										size="small"
										label={`${cfg.rotulo}: ${n}`}
										sx={{ bgcolor: cfg.fundo, color: cfg.cor, fontWeight: 600 }}
									/>
								);
							})}
						</Box>

						{colunasIgnoradas.length > 0 && (
							<Alert severity="info">
								Colunas não reconhecidas e ignoradas: {colunasIgnoradas.join(", ")}.
							</Alert>
						)}

						{alertasExtras?.(previa)}

						{resumo.invalidos > 0 && (
							<Alert severity="warning">
								As linhas com erro não serão importadas. Corrija na planilha e envie de novo, ou importe as demais agora.
							</Alert>
						)}

						{previa.linhas.some((l) => (l.avisos || []).length > 0) && <Alert severity="warning">{textoAvisos}</Alert>}

						{totalAImportar === 0 && (
							<Alert severity="info">Nenhum {entidade.singular} novo para importar nesta planilha.</Alert>
						)}

						<TableContainer sx={{ maxHeight: 420, border: "1px solid #dfe3e8", borderRadius: "10px" }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell sx={{ ...cabecalhoSx, bgcolor: "#f0f5f4" }}>Linha</TableCell>
										{colunas.map((c) => (
											<TableCell key={c.rotulo} sx={{ ...cabecalhoSx, bgcolor: "#f0f5f4" }}>
												{c.rotulo}
											</TableCell>
										))}
										<TableCell sx={{ ...cabecalhoSx, bgcolor: "#f0f5f4" }}>Situação</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{previa.linhas.map((l) => {
										const cfg = SITUACAO[l.status] || SITUACAO.invalido;
										return (
											<TableRow key={l.linha} sx={{ opacity: l.status === "novo" || l.status === "reativar" ? 1 : 0.75 }}>
												<TableCell>{l.linha}</TableCell>
												{colunas.map((c) => (
													<TableCell key={c.rotulo} sx={c.sx}>
														{c.render(l)}
													</TableCell>
												))}
												<TableCell>
													<Tooltip title={l.motivo || ""} disableHoverListener={!l.motivo}>
														<Chip size="small" label={cfg.rotulo} sx={{ bgcolor: cfg.fundo, color: cfg.cor, fontWeight: 500 }} />
													</Tooltip>
													{l.motivo && (
														<Typography variant="caption" sx={{ color: cfg.cor, display: "block", mt: 0.5 }}>
															{l.motivo}
														</Typography>
													)}
													{(l.avisos || []).map((aviso, i) => (
														<Typography key={i} variant="caption" sx={{ color: "#b26a00", display: "block", mt: 0.5 }}>
															{aviso}
														</Typography>
													))}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				)}

				{etapa === "resultado" && resultado && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 2 }}>
						<CheckCircleIcon sx={{ fontSize: 56, color: "#1e8e4e" }} />
						<Typography variant="h6" sx={{ color: "#0f4557", fontWeight: 700 }}>
							Importação concluída
						</Typography>
						<Typography variant="body2" sx={{ color: "#0f4557" }}>
							{resultado.criados} {entidade.contagem} cadastrado(s)
							{resultado.reativados ? ` e ${resultado.reativados} reativado(s)` : ""}.
						</Typography>
						{(resultado.resumo.jaCadastrados > 0 || resultado.resumo.repetidos > 0 || resultado.resumo.invalidos > 0) && (
							<Typography variant="body2" sx={{ color: "#5a7a88", textAlign: "center" }}>
								Ignorados: {resultado.resumo.jaCadastrados} já cadastrado(s), {resultado.resumo.repetidos} repetido(s) na planilha
								e {resultado.resumo.invalidos} com erro.
							</Typography>
						)}
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				{etapa === "previa" && (
					<Button onClick={reiniciar} disabled={carregando} sx={{ color: "#5a7a88", borderRadius: "10px", mr: "auto" }}>
						Escolher outro arquivo
					</Button>
				)}
				<Button onClick={fechar} disabled={carregando} sx={{ color: "#5a7a88", borderRadius: "10px" }}>
					{etapa === "resultado" ? "Fechar" : "Cancelar"}
				</Button>
				{etapa === "previa" && (
					<Button
						onClick={handleImportar}
						variant="contained"
						disabled={carregando || totalAImportar === 0}
						startIcon={carregando ? <CircularProgress size={18} sx={{ color: "white" }} /> : null}
						sx={{ bgcolor: "#1a9e8f", "&:hover": { bgcolor: "#138a7d" }, borderRadius: "10px", textTransform: "none", fontWeight: 600 }}
					>
						{carregando ? "Importando..." : `Importar ${totalAImportar} ${entidade.contagem}`}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default ImportarPlanilhaDialog;
